import type { Form } from '../services/formService';
import type { Task } from '../types/Task';

export interface AssetInfo {
  name: string;
  assetNumber: string;
}

/**
 * Extracts asset information from form fields using the same logic as FormSummaryView
 * This ensures consistency between summary and detailed views
 */
export function extractAssetFromFormFields(fields: Form['fields']): AssetInfo | null {
  // Check for Rest Area first (highest priority)
  const restAreaNumberField = fields.find((f: Form['fields'][0]) => f.field === 'RestAreaNumber 1');
  const restAreaNameField = fields.find((f: Form['fields'][0]) => f.field === 'RestArea 1');
  
  // Check if either RestAreaNumber OR RestArea is answered
  if ((restAreaNumberField && restAreaNumberField.value) || (restAreaNameField && restAreaNameField.value)) {
    // Rest Area case: "Name (Number)" format if both exist
    let displayName = '';
    let assetNumber = '';
    
    // Determine the asset number
    if (restAreaNumberField && restAreaNumberField.value) {
      assetNumber = restAreaNumberField.value;
    } else if (restAreaNameField && restAreaNameField.value) {
      assetNumber = restAreaNameField.value;
    }
    
    // Determine the display name
    if (restAreaNameField && restAreaNameField.value && restAreaNumberField && restAreaNumberField.value) {
      // Both name and number exist: "Name (Number)"
      displayName = `${restAreaNameField.value} (${restAreaNumberField.value})`;
    } else if (restAreaNameField && restAreaNameField.value) {
      // Only name exists
      displayName = restAreaNameField.value;
    } else if (restAreaNumberField && restAreaNumberField.value) {
      // Only number exists
      displayName = restAreaNumberField.value;
    }
    
    return {
      name: displayName,
      assetNumber: assetNumber
    };
  }
  
  // Check for Road only (second priority)
  // Collect ALL road numbers and names (for multiple Roads & Accomplishments rows)
  const roadNameFields = fields.filter((f: Form['fields'][0]) => f.field === 'RoadNameWinter' && f.value);
  const roadNumberFields = fields.filter((f: Form['fields'][0]) => f.field === 'RoadNumberWinter' && f.value);
  
  if (roadNumberFields.length > 0) {
    // Sort road fields by their path to preserve order (Roads.0, Roads.1, etc.)
    roadNumberFields.sort((a, b) => a.path.localeCompare(b.path));
    
    // Match each road number with its corresponding road name
    const roadPairs: Array<{ number: string; name: string }> = [];
    roadNumberFields.forEach(roadNumField => {
      const roadNum = roadNumField.value;
      // Extract the base path of the road section (up to but not including the field name)
      // e.g., "WorkCompleted.Tasks 1.5.Tasks.Roads.0.WinterRoad" from "WorkCompleted.Tasks 1.5.Tasks.Roads.0.WinterRoad.RoadNumberWinter"
      const roadPath = roadNumField.path.substring(0, roadNumField.path.lastIndexOf('.'));
      
      // Find the corresponding road name field in the same road section
      const matchingRoadNameField = roadNameFields.find(rnField => {
        const roadNamePath = rnField.path.substring(0, rnField.path.lastIndexOf('.'));
        return roadNamePath === roadPath;
      });
      
      const roadName = matchingRoadNameField?.value || '';
      roadPairs.push({ number: roadNum, name: roadName });
    });
    
    // Build display name: if we have multiple roads, show them comma-separated
    // Don't include road numbers in brackets - only show names or numbers, not both
    if (roadPairs.length === 1) {
      // Single road: use the name if available, otherwise just the number
      const road = roadPairs[0];
      const displayName = road.name || road.number;
      return {
        name: displayName,
        // Set assetNumber to match name to prevent brackets from being added in display
        assetNumber: displayName
      };
    } else {
      // Multiple roads: create comma-separated list
      // Try to use names if available, otherwise use numbers
      const displayParts = roadPairs.map(road => road.name || road.number);
      const displayName = displayParts.join(', ');
      
      return {
        name: displayName,
        // Set assetNumber to match name to prevent brackets from being added in display
        assetNumber: displayName
      };
    }
  }
  
  // Check for Major Structure (third priority)
  const structureNumberField = fields.find((f: Form['fields'][0]) => f.field === 'MajorStructureNumb 1');
  const structureNameField = fields.find((f: Form['fields'][0]) => f.field === 'MajorStructure 1');
  
  if (structureNumberField && structureNumberField.value) {
    let displayName = '';
    
    // If both name and number exist, show "Name (Number)"
    if (structureNameField && structureNameField.value && structureNumberField.value) {
      displayName = `${structureNameField.value} (${structureNumberField.value})`;
    } else {
      // Fallback to whichever is available
      displayName = structureNameField?.value || structureNumberField.value;
    }
    
    return {
      name: displayName,
      assetNumber: structureNumberField.value
    };
  }
  
  // Fallback: Look for any other asset-related name fields
  const assetNameFields = fields.filter((f: Form['fields'][0]) => 
    f.field.toLowerCase().includes('name') && 
    (f.field.toLowerCase().includes('road') || 
     f.field.toLowerCase().includes('structure') || 
     f.field.toLowerCase().includes('rest') || 
     f.field.toLowerCase().includes('asset')) &&
    f.value && f.value.trim() !== ''
  );
  
  if (assetNameFields.length > 0) {
    const firstAssetNameField = assetNameFields[0];
    return {
      name: firstAssetNameField.value,
      assetNumber: firstAssetNameField.value
    };
  }
  
  return null;
}

/**
 * Gets asset information for a task with fallback logic
 * 1. First tries task.taskLocations[0].asset
 * 2. Falls back to form field extraction for ALL fields (since we don't have task group info here)
 */
export function getAssetForTask(task: Task, formFields: Form['fields']): AssetInfo | null {
  // First try task locations
  if (task.taskLocations && task.taskLocations.length > 0 && task.taskLocations[0].asset) {
    const firstAsset = task.taskLocations[0].asset;
    return {
      name: firstAsset.name,
      assetNumber: firstAsset.assetNumber
    };
  }
  
  // Fallback to form field extraction from ALL form fields
  // This will work because the form typically only has one rest area per form submission
  return extractAssetFromFormFields(formFields);
}

/**
 * Gets asset information for a specific task group within a form
 * This handles the case where a task group might have multiple road entries
 */
export function getAssetForTaskGroup(taskGroupId: string, formFields: Form['fields']): AssetInfo | null {
  // Filter fields to only those in this task group
  // IMPORTANT: RestArea fields can be deeply nested under Roads subgroups,
  // e.g., "WorkCompleted.Tasks 1.5.Tasks.Roads.0.WinterRoad.LocationDetailsRes 1.RestAreaNumber 1"
  // So we need to match all fields that START with the task group path
  const taskGroupPath = `WorkCompleted.Tasks ${taskGroupId}`;
  
  const taskGroupFields = formFields.filter((field: Form['fields'][0]) => {
    return field.path.startsWith(taskGroupPath);
  });
  
  return extractAssetFromFormFields(taskGroupFields);
}
